import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mediaService } from "../services/mediaService";
import { MediaComment, CreateMediaCommentDto, UpdateMediaCommentDto } from "../types/media";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import Session from "supertokens-auth-react/recipe/session";

export const mediaCommentKeys = {
  all: ['mediaComments'] as const,
  lists: () => [...mediaCommentKeys.all, 'list'] as const,
  list: (ownerId: string, albumId: string, mediaId: string) => [...mediaCommentKeys.lists(), ownerId, albumId, mediaId] as const,
};

export const useMediaComments = (ownerId: string, albumId: string, mediaId: string, offset: number = 0, limit: number = 10) => {
  const queryClient = useQueryClient();

  const {
    data: comments = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [...mediaCommentKeys.list(ownerId, albumId, mediaId), offset, limit],
    queryFn: async () => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("Missing required parameters or user not authenticated");
      }
      return mediaService.getMediaComments(ownerId, albumId, mediaId, offset, limit);
    },
    enabled: !!(ownerId && albumId && mediaId),
  });

  const createCommentMutation = useMutation({
    mutationFn: async (commentData: CreateMediaCommentDto) => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("Missing required parameters or user not authenticated");
      }
      return mediaService.createMediaComment(ownerId, albumId, mediaId, commentData);
    },
    onSuccess: (newComment) => {
      queryClient.setQueryData<MediaComment[]>(
        [...mediaCommentKeys.list(ownerId, albumId, mediaId), offset, limit],
        (old) => {
          return old ? [...old, newComment] : [newComment];
        }
      );
      queryClient.invalidateQueries({ queryKey: mediaCommentKeys.list(ownerId, albumId, mediaId) });
    },
    onError: (error) => {
      console.error("Error creating comment:", handleApiError(error));
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, updateData }: { commentId: string; updateData: UpdateMediaCommentDto }) => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("Missing required parameters or user not authenticated");
      }
      return mediaService.updateMediaComment(ownerId, albumId, mediaId, commentId, updateData);
    },
    onSuccess: (updatedComment) => {
      queryClient.setQueryData<MediaComment[]>(
        [...mediaCommentKeys.list(ownerId, albumId, mediaId), offset, limit],
        (old) => {
          return old ? old.map((comment) => (comment.id === updatedComment.id ? updatedComment : comment)) : [updatedComment];
        }
      );
      queryClient.invalidateQueries({ queryKey: mediaCommentKeys.list(ownerId, albumId, mediaId) });
    },
    onError: (error) => {
      console.error("Error updating comment:", handleApiError(error));
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("Missing required parameters or user not authenticated");
      }
      return mediaService.deleteMediaComment(ownerId, albumId, mediaId, commentId);
    },
    onSuccess: (_, commentId) => {
      queryClient.setQueryData<MediaComment[]>(
        [...mediaCommentKeys.list(ownerId, albumId, mediaId), offset, limit],
        (old) => {
          return old ? old.filter((comment) => comment.id !== commentId) : [];
        }
      );
      queryClient.invalidateQueries({ queryKey: mediaCommentKeys.list(ownerId, albumId, mediaId) });
    },
    onError: (error) => {
      console.error("Error deleting comment:", handleApiError(error));
    },
  });

  return {
    // Data
    comments,
    hasComments: comments.length > 0,

    // Loading states
    isLoading,
    isCreating: createCommentMutation.isPending,
    isUpdating: updateCommentMutation.isPending,
    isDeleting: deleteCommentMutation.isPending,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : null,

    // Actions
    createComment: createCommentMutation.mutateAsync,
    updateComment: (commentId: string, updateData: UpdateMediaCommentDto) =>
      updateCommentMutation.mutateAsync({ commentId, updateData }),
    deleteComment: deleteCommentMutation.mutateAsync,
    refreshComments: async () => { await refetch(); },
    clearError: () => {
      queryClient.resetQueries({ queryKey: mediaCommentKeys.list(ownerId, albumId, mediaId) });
    },

    // Query client for manual invalidation
    invalidateComments: () => queryClient.invalidateQueries({ queryKey: mediaCommentKeys.list(ownerId, albumId, mediaId) }),
  };
};